import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CATEGORY_META,
  normalizeCategoryValue,
  resolveCategoryMeta,
} from "./categoryDirectoryConfig";

const configuredApiBase =
  typeof process !== "undefined" &&
  process.env
    ? process.env
        .REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const API_BASE_URL = (
  configuredApiBase ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

/*
 * These are the actual business categories accepted by
 * the GoOurHub Business API.
 *
 * Anything else coming from the directory endpoint
 * (countries, communities, bad imported values, etc.)
 * is ignored.
 */
const ALLOWED_BUSINESS_CATEGORY_VALUES =
  new Set(
    [
      "accommodation",
      "beauty",
      "boutique",
      "catering",
      "child care",
      "clothing",
      "construction",
      "consulting",
      "design",
      "education",
      "electrical",
      "elderly care",
      "employment",
      "entertainment",
      "event management",
      "financial",
      "fitness training",
      "gardening",
      "health",
      "home help",
      "it services",
      "jewelry",
      "kosher",
      "legal",
      "legal advice",
      "marketing",
      "personal shopping",
      "pet care",
      "photography",
      "plumbing",
      "realtors",
      "repair services",
      "restaurants",
      "groceries",
      "foodtruck",
      "sports",
      "storage",
      "student services",
      "tailor",
      "translation",
      "transportation",
      "tutoring",
      "vegan",
      "venues",
      "worship places",
      "leadership coaching",
      "corporate training",
      "youth leadership",
    ].map((value) =>
      normalizeCategoryValue(value)
    )
  );

const SPECIAL_CATEGORY_VALUES =
  new Set([
    "multiple",
    "others",
  ]);

const categoryValueFromItem = (
  item
) => {
  if (typeof item === "string") {
    return item;
  }

  if (
    !item ||
    typeof item !== "object"
  ) {
    return "";
  }

  return (
    item.value ||
    item.category ||
    item.slug ||
    item.name ||
    item.label ||
    item.key ||
    ""
  );
};

const normalizeOptions = (
  items
) => {
  const unique = new Map();

  (
    Array.isArray(items)
      ? items
      : []
  ).forEach((item) => {
    const rawValue =
      categoryValueFromItem(item);

    if (!rawValue) {
      return;
    }

    /*
     * Validate the raw value BEFORE resolving metadata.
     * This prevents countries/communities/bad imported
     * values from becoming dropdown choices.
     */
    const rawNormalized =
      normalizeCategoryValue(
        rawValue
      );

    if (
      !rawNormalized ||
      SPECIAL_CATEGORY_VALUES.has(
        rawNormalized
      ) ||
      !ALLOWED_BUSINESS_CATEGORY_VALUES.has(
        rawNormalized
      )
    ) {
      return;
    }

    const meta =
      resolveCategoryMeta(
        rawNormalized
      );

    const normalizedValue =
      normalizeCategoryValue(
        meta.value
      );

    if (
      !normalizedValue ||
      !ALLOWED_BUSINESS_CATEGORY_VALUES.has(
        normalizedValue
      )
    ) {
      return;
    }

    unique.set(
      normalizedValue,
      {
        ...meta,
        value:
          normalizedValue,
      }
    );
  });

  return Array.from(
    unique.values()
  ).sort(
    (left, right) =>
      left.label.localeCompare(
        right.label,
        undefined,
        {
          sensitivity: "base",
        }
      )
  );
};

const FALLBACK_OPTIONS =
  normalizeOptions(
    CATEGORY_META
  );

/**
 * Database-driven category dropdown.
 *
 * The API can contain historical or incorrectly imported
 * category values, so only official GoOurHub business
 * categories are displayed here.
 */
function BusinessCategorySelect({
  id = "ps-bus-type",
  name = "business_type",
  value = "",
  onChange,
  required = false,
  className,
  disabled = false,
  includeSpecialOptions = true,
}) {
  const [
    directoryOptions,
    setDirectoryOptions,
  ] = useState(
    FALLBACK_OPTIONS
  );

  const [
    isLoading,
    setIsLoading,
  ] = useState(true);

  const [
    loadFailed,
    setLoadFailed,
  ] = useState(false);

  useEffect(() => {
    const controller =
      new AbortController();

    const loadCategories =
      async () => {
        setIsLoading(true);
        setLoadFailed(false);

        try {
          const response =
            await fetch(
              `${API_BASE_URL}/api/directory/categories`,
              {
                method:
                  "GET",
                headers: {
                  Accept:
                    "application/json",
                },
                signal:
                  controller.signal,
              }
            );

          let payload;

          try {
            payload =
              await response.json();
          } catch {
            throw new Error(
              "The category directory returned invalid JSON."
            );
          }

          if (
            !response.ok ||
            payload?.success ===
              false
          ) {
            throw new Error(
              payload?.message ||
                `Unable to load business categories (${response.status}).`
            );
          }

          const rawCategories =
            Array.isArray(
              payload?.categories
            )
              ? payload.categories
              : payload?.categories &&
                  typeof payload.categories ===
                    "object"
                ? Object.entries(
                    payload.categories
                  ).map(
                    ([
                      category,
                      count,
                    ]) => ({
                      category,
                      count,
                    })
                  )
                : [];

          const nextOptions =
            normalizeOptions(
              rawCategories
            );

          if (
            nextOptions.length ===
            0
          ) {
            throw new Error(
              "No public business categories were returned."
            );
          }

          if (
            !controller.signal
              .aborted
          ) {
            setDirectoryOptions(
              nextOptions
            );
          }
        } catch (error) {
          if (
            error.name !==
              "AbortError" &&
            !controller.signal
              .aborted
          ) {
            console.error(
              "Unable to load Partner Sign Up categories.",
              error
            );

            setLoadFailed(
              true
            );

            setDirectoryOptions(
              FALLBACK_OPTIONS
            );
          }
        } finally {
          if (
            !controller.signal
              .aborted
          ) {
            setIsLoading(
              false
            );
          }
        }
      };

    loadCategories();

    return () =>
      controller.abort();
  }, []);

  const options =
    useMemo(() => {
      const unique =
        new Map(
          directoryOptions.map(
            (option) => [
              normalizeCategoryValue(
                option.value
              ),
              option,
            ]
          )
        );

      const selectedValue =
        normalizeCategoryValue(
          value
        );

      /*
       * Only preserve the selected value if it is
       * still a legitimate business category.
       *
       * This also prevents an old accidentally saved
       * country value from being reinserted.
       */
      if (
        selectedValue &&
        ALLOWED_BUSINESS_CATEGORY_VALUES.has(
          selectedValue
        ) &&
        !SPECIAL_CATEGORY_VALUES.has(
          selectedValue
        ) &&
        !unique.has(
          selectedValue
        )
      ) {
        const selectedMeta =
          resolveCategoryMeta(
            selectedValue
          );

        unique.set(
          selectedValue,
          {
            ...selectedMeta,
            value:
              selectedValue,
          }
        );
      }

      return Array.from(
        unique.values()
      ).sort(
        (left, right) =>
          left.label.localeCompare(
            right.label,
            undefined,
            {
              sensitivity:
                "base",
            }
          )
      );
    }, [
      directoryOptions,
      value,
    ]);

  return (
    <>
      <select
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className={className}
        disabled={disabled}
        aria-busy={
          isLoading
        }
      >
        <option value="">
          Select business type
        </option>

        {options.map(
          (option) => (
            <option
              key={
                option.value
              }
              value={
                option.value
              }
            >
              {option.icon}{" "}
              {option.label}
            </option>
          )
        )}

        {includeSpecialOptions && (
          <>
            <option value="multiple">
              🧩 Multiple
              categories
            </option>

            <option value="others">
              ➕ Other business
              type
            </option>
          </>
        )}
      </select>

      {loadFailed && (
        <small
          role="status"
          className="ps-field__hint"
        >
          The live category
          list could not be
          refreshed. The
          current saved
          GoOurHub category
          list is being used.
        </small>
      )}
    </>
  );
}

export default BusinessCategorySelect;